import { Board, BoardColumn, BoardStatus, Issue, boardProjectKeys } from "./types";

// A board column resolved against the current status set: the configured column plus the actual
// BoardStatus objects it aggregates (for rendering dots, names, done-detection downstream).
export interface ResolvedColumn {
  id: string;
  name: string;
  statusIds: string[];
  statuses: BoardStatus[];
  orderIndex: number;
}

// The statuses available to a board = every status owned by one of the board's projects.
export function boardAvailableStatuses(board: Board, allStatuses: BoardStatus[]): BoardStatus[] {
  const projectKeys: Set<string> = new Set<string>(boardProjectKeys(board));
  return allStatuses.filter((status: BoardStatus) => projectKeys.has(status.projectKey));
}

// The columns a board renders, in order. When the board has explicit columns, each maps to the
// subset of available statuses it references (dangling ids are dropped). When it has none, the
// board falls back to one column per available status — keyed by the status id so the column id
// is stable across reloads (WIP limits / query caches hang off it).
export function boardColumns(board: Board, allStatuses: BoardStatus[]): ResolvedColumn[] {
  const available: BoardStatus[] = boardAvailableStatuses(board, allStatuses);
  const byId: Map<string, BoardStatus> = new Map<string, BoardStatus>(
    available.map((status: BoardStatus) => [status.id, status])
  );
  const configured: BoardColumn[] | undefined = board.columns;
  if (!configured || configured.length === 0) {
    return available.map((status: BoardStatus, index: number): ResolvedColumn => ({
      id: status.id,
      name: status.name,
      statusIds: [status.id],
      statuses: [status],
      orderIndex: index
    }));
  }
  return [...configured]
    .sort((a: BoardColumn, b: BoardColumn) => a.orderIndex - b.orderIndex)
    .map((column: BoardColumn): ResolvedColumn => ({
      id: column.id,
      name: column.name,
      statusIds: column.statusIds,
      statuses: column.statusIds
        .map((id: string) => byId.get(id))
        .filter((status: BoardStatus | undefined): status is BoardStatus => status !== undefined),
      orderIndex: column.orderIndex
    }));
}

// Which concrete status a card lands in when dropped into a column: keep its current status if that
// status already belongs to the column (a reorder within the lane), otherwise the column's first
// status. Returns null only for an empty column (nothing to move into).
export function resolveDropStatus(column: ResolvedColumn, currentStatusId: string): string | null {
  if (column.statusIds.includes(currentStatusId)) return currentStatusId;
  return column.statusIds.length > 0 ? column.statusIds[0]! : null;
}

// The column id that holds a given status, or null when the status is mapped to no column.
export function columnIdForStatus(columns: ResolvedColumn[], statusId: string): string | null {
  const column: ResolvedColumn | undefined = columns.find((c: ResolvedColumn) => c.statusIds.includes(statusId));
  return column ? column.id : null;
}

// True when the board leaves at least one occupied status unmapped — i.e. some issues would
// otherwise vanish from the column view.
export function hasHiddenIssues(board: Board, allStatuses: BoardStatus[], issues: Issue[]): boolean {
  return hiddenIssueCount(board, allStatuses, issues) > 0;
}

// How many of the given issues sit in a status that no column on this board maps.
export function hiddenIssueCount(board: Board, allStatuses: BoardStatus[], issues: Issue[]): number {
  const columns: ResolvedColumn[] = boardColumns(board, allStatuses);
  const mapped: Set<string> = new Set<string>();
  for (const column of columns) {
    for (const statusId of column.statusIds) mapped.add(statusId);
  }
  return issues.filter((issue: Issue) => !mapped.has(issue.statusId)).length;
}
